import { Alert, Button, Card, PageHeader, todayInArgentina } from '@catering/ui';
import { Printer } from 'lucide-react';
import { type JSX, useState } from 'react';

import { ApiError } from '../../api/client';
import { printDeliveryNotesBatch } from '../../delivery-notes/api';
import { mapDeliveryNoteError } from '../../delivery-notes/delivery-note-api-errors';
import { OperatingUnitSelect } from '../../components/operating-unit-select';
import { GuideLink } from '../../guide/guide-link';

function todayIso(): string {
  return todayInArgentina();
}

/**
 * `delivery-note-documents/spec.md` → "Impresión por lote de una fecha
 * genera un único PDF con los remitos pendientes de despacho", from the UI
 * (`design.md` §8.3/§9.2): a single multi-page PDF, never a ZIP.
 */
export function DeliveryNotePrintBatchPage(): JSX.Element {
  const [serviceDate, setServiceDate] = useState(todayIso());
  const [operatingUnitId, setOperatingUnitId] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function handlePrint(): Promise<void> {
    setError(undefined);
    setDownloading(true);

    try {
      await printDeliveryNotesBatch(serviceDate, operatingUnitId);
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status < 500
          ? mapDeliveryNoteError(caught)
          : 'No pudimos generar el PDF del lote. Intentá de nuevo.',
      );
    } finally {
      setDownloading(false);
    }
  }

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Printer}
        title="Imprimir remitos del día"
        description="Descargá un PDF con los remitos de la fecha que todavía no están firmados."
        actions={<GuideLink moduleId="delivery-notes" />}
      />

      <Card>
        <div className="flex flex-col gap-4">
          {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Fecha de servicio</span>
            <input
              type="date"
              value={serviceDate}
              onChange={(event) => setServiceDate(event.target.value)}
              className="h-11 w-64 rounded-md border border-slate-300 px-3 text-sm"
            />
          </label>

          <OperatingUnitSelect
            id="delivery-note-batch-operating-unit"
            label="Punto de venta"
            allLabel="Todos los puntos"
            value={operatingUnitId}
            onChange={setOperatingUnitId}
          />

          <div>
            <Button icon={Printer} loading={downloading} onClick={() => void handlePrint()}>
              Descargar PDF
            </Button>
          </div>
        </div>
      </Card>
    </section>
  );
}
